import React from 'react';
import AvatarEditor from 'react-avatar-editor';
import ConfirmDialog from '../../components/ConfirmDialog';
import PasswordInput from '../../components/PasswordInput';
import { AccountProps } from './types';
import { useAccount } from './useAccount';
import { formatFileSize } from '../../utils/formatters';
import { IMAGE_UPLOAD_ACCEPT } from '../../utils/imageFormat';
import { useIsMobile } from '../../config/breakpoints';
import styles from './Account.module.css';

/**
 * アカウント管理ページコンポーネント
 */
const Account: React.FC<AccountProps> = ({ currentUser, setCurrentUser }) => {
  const {
    windowWidth,
    profileData,
    profileMessage,
    passwordData,
    passwordMessage,
    iconFile,
    editorOpen,
    zoom,
    rotation,
    position,
    editorRef,
    imageSize,
    originalFile,
    compressedSize,
    showDeleteConfirm,
    deletingAccount,
    iconUrl,
    getCurrentHospitalName,
    handleProfileChange,
    handleProfileSubmit,
    handlePasswordChange,
    handlePasswordSubmit,
    handleIconChange,
    handleZoomChange,
    handleRotationChange,
    handlePositionChange,
    handleSaveEdit,
    handleCancelEdit,
    handleIconSubmit,
    handleDeleteAccount,
    setShowDeleteConfirm
  } = useAccount({ currentUser, setCurrentUser });

  const isMobile = useIsMobile();

  return (
    <div className={`${styles['account-container']} ${isMobile ? styles['account-container-responsive'] : ''}`}>
      <h2 className={styles['account-header']}>アカウント管理</h2>

      <div className={`${styles['account-cards-container']} ${isMobile ? '' : styles['account-responsive-cards']}`}>
        {/* プロフィール更新セクション */}
        <div className={styles['account-card']}>
          <div className={styles['account-card-header-container']}>
            <h3 className={styles['account-card-header']}>プロフィール</h3>
            {profileMessage && <p className={profileMessage.includes('失敗') ? styles['account-error-message'] : styles['account-success-message']}>{profileMessage}</p>}
          </div>
          
          <div className={styles['account-profile-info-container']}>
            {iconUrl ? (
              <div className={styles['account-profile-image-container']}>
                <img
                  className={styles['account-profile-image']}
                  src={iconUrl}
                  alt="プロフィール画像"
                />
              </div>
            ) : (
              <div className={styles['account-profile-image-placeholder']}>
                <span className={styles['account-profile-initial']}>{currentUser.name ? currentUser.name.charAt(0).toUpperCase() : '?'}</span>
              </div>
            )}
            
            <div className={styles['account-profile-details']}>
              <div className={styles['account-profile-item']}>
                <span className={styles['account-profile-label']}>メールアドレス</span>
                <span className={styles['account-profile-value']}>{currentUser.email}</span>
              </div>
              
              <div className={styles['account-profile-item']}>
                <span className={styles['account-profile-label']}>役割</span>
                <span className={styles['account-profile-value']}>{currentUser.role || '未設定'}</span>
              </div>
              
              <div className={styles['account-profile-item']}>
                <span className={styles['account-profile-label']}>所属病院</span>
                <span className={styles['account-profile-value']}>{getCurrentHospitalName()}</span>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleProfileSubmit} className={styles['account-form']}>
            <div className={styles['account-form-group']}>
              <label className={styles['account-label']}>名前</label>
              <input
                type="text"
                name="name"
                value={profileData.name}
                onChange={handleProfileChange}
                className={styles['account-input']}
                placeholder="名前を入力"
              />
            </div>
            <button type="submit" className={styles['account-button']}>
              <span className={styles['account-button-text']}>更新する</span>
            </button>
          </form>
        </div>

        {/* パスワード変更セクション */}
        <div className={styles['account-card']}>
          <div className={styles['account-card-header-container']}>
            <h3 className={styles['account-card-header']}>パスワード変更</h3>
            {passwordMessage && <p className={passwordMessage.includes('失敗') ? styles['account-error-message'] : styles['account-success-message']}>{passwordMessage}</p>}
          </div>
          
          <form onSubmit={handlePasswordSubmit} className={styles['account-form']}>
            <div className={styles['account-form-group']}>
              <label className={styles['account-label']}>現在のパスワード</label>
              <PasswordInput
                id="oldPassword"
                name="oldPassword"
                value={passwordData.oldPassword}
                onChange={handlePasswordChange}
                placeholder="現在のパスワード"
                className={styles['account-input']}
                autoComplete="current-password"
                data-testid="account__old-password-input"
              />
            </div>
            <div className={styles['account-form-group']}>
              <label className={styles['account-label']}>新しいパスワード</label>
              <PasswordInput
                id="newPassword"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                placeholder="新しいパスワード"
                className={styles['account-input']}
                autoComplete="new-password"
                data-testid="account__new-password-input"
              />
            </div>
            <button type="submit" className={styles['account-button']}>
              <span className={styles['account-button-text']}>変更する</span>
            </button>
          </form>
        </div>

        {/* アイコン更新セクション */}
        <div className={styles['account-card']}>
          <div className={styles['account-card-header-container']}>
            <h3 className={styles['account-card-header']}>アイコン更新</h3>
          </div>

          <div className={styles['account-icon-editor-container']}>
            {editorOpen ? (
              <div className={styles['account-editor-container']}>
                <div className={styles['account-avatar-editor-wrapper']}>
                  <AvatarEditor
                    ref={editorRef}
                    image={originalFile as File}
                    width={250}
                    height={250}
                    border={50}
                    borderRadius={125}
                    color={[255, 255, 255, 0.6]} // RGBA
                    scale={zoom}
                    rotate={rotation}
                    position={position}
                    onPositionChange={handlePositionChange}
                    className={styles['account-avatar-editor']}
                  />
                </div>
                
                <div className={styles['account-editor-controls']}>
                  <div className={styles['account-control-group']}>
                    <label className={styles['account-control-label']}>ズーム</label>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.01"
                      value={zoom}
                      onChange={handleZoomChange}
                      className={styles['account-slider']}
                    />
                  </div>
                  
                  <div className={styles['account-control-group']}>
                    <label className={styles['account-control-label']}>回転</label>
                    <input
                      type="range"
                      min="0"
                      max="360"
                      step="1"
                      value={rotation}
                      onChange={handleRotationChange}
                      className={styles['account-slider']}
                    />
                  </div>
                  
                  {imageSize && (
                    <div className={styles['account-file-size-info']}>
                      <span>画像サイズ: {formatFileSize(imageSize)}</span>
                      {imageSize > 1024 * 1024 && (
                        <span className={styles['account-compression-note']}>
                          （アップロード時に1MB以下に圧縮されます）
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className={styles['account-editor-button-group']}>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className={styles['account-cancel-button']}
                    >
                      キャンセル
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveEdit}
                      className={styles['account-save-button']}
                    >
                      確定
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* 既にアイコンがある場合はプレビュー表示 */}
                <div className={styles['account-icon-preview-wrapper']}>
                  {iconFile ? (
                    <div className={styles['account-icon-preview-container']}>
                      <img
                        className={styles['account-icon-preview']}
                        src={URL.createObjectURL(iconFile)}
                        alt="選択されたアイコン"
                      />
                      {compressedSize && (
                        <div className={styles['account-file-size-info']}>
                          <span>サイズ: {formatFileSize(compressedSize)}</span>
                        </div>
                      )}
                    </div>
                  ) : iconUrl ? (
                    <div className={styles['account-icon-preview-container']}>
                      <img
                        className={styles['account-icon-preview']}
                        src={iconUrl}
                        alt="現在のアイコン"
                      />
                    </div>
                  ) : (
                    <div className={styles['account-icon-preview-placeholder']}>
                      <span>画像なし</span>
                    </div>
                  )}
                </div>
                
                <form onSubmit={handleIconSubmit} className={styles['account-icon-form']}>
                  <div className={styles['account-file-input-container']}>
                    <label className={styles['account-file-input-label']}>
                      <svg className={styles['account-upload-icon']} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                        <path fill="currentColor" d="M12 16l4-5h-3V4h-2v7H8l4 5zm9-9v11c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V7c0-1.1.9-2 2-2h3.93c.7 0 1.3-.4 1.6-1l.94-1.7c.2-.4.6-.5 1-.5h2.13c.4 0 .8.2 1 .5l.93 1.7c.3.6.9 1 1.6 1H19c1.1 0 2 .9 2 2zm-5 8c0-2.8-2.2-5-5-5s-5 2.2-5 5 2.2 5 5 5 5-2.2 5-5zm-5-3c1.7 0 3 1.3 3 3s-1.3 3-3 3-3-1.3-3-3 1.3-3 3-3z"/>
                      </svg>
                      <span className={styles['account-file-input-text']}>画像を選択</span>
                      <input 
                        id="icon-upload"
                        type="file" 
                        accept={IMAGE_UPLOAD_ACCEPT}
                        onChange={handleIconChange} 
                        className={styles['account-file-input']} 
                      />
                    </label>
                  </div>
                  {iconFile && (
                    <button type="submit" className={styles['account-upload-button']}>
                      <span className={styles['account-button-text']}>アップロード</span>
                    </button>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* アカウント削除セクション */}
      <div className={styles['account-section']}>
        <h4 className={styles['account-danger-zone-title']}>アカウントを削除する</h4>
        <p className={styles['account-danger-zone-description']}>
          アカウントを削除すると、あなたの個人情報やコメント履歴などが非表示になります。この操作は取り消せません。
        </p>
        <button 
          className={styles['account-delete-account-button']} 
          onClick={() => setShowDeleteConfirm(true)}
          disabled={deletingAccount}
        >
          {deletingAccount ? 'アカウント削除中...' : 'アカウントを削除'}
        </button>
      </div>
      
      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="アカウント削除の確認"
        message="本当にアカウントを削除しますか？この操作は取り消せません。"
        confirmLabel="削除する"
        cancelLabel="キャンセル"
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default Account; 
